import { useState, useEffect, useRef, useCallback } from 'react';
import { CoreServiceSelector } from '../../../services/core/service-selector';
import { validate, initFields, ERROR_ORDER } from './validation';

export function useSignupForm(dealId, vendorId, getRecaptchaToken, scrollRef) {
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [venueInfo, setVenueInfo] = useState(null);

  const [f, setF] = useState(null);
  const [accept, setAccept] = useState(false);
  const [err, setErr] = useState({});
  const [triedSubmit, setTriedSubmit] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const refs = useRef({});
  const r = useCallback((key) => (el) => { refs.current[key] = el; }, []);
  const submittingRef = useRef(false);

  useEffect(() => {
    if (!dealId || !vendorId) return;
    (async () => {
      try {
        const captchaToken = getRecaptchaToken ? await getRecaptchaToken() : null;
        const data = await CoreServiceSelector.GetSignupInfoService().getVendorSignupInfo(dealId, vendorId, captchaToken);
        if (data?.isAlreadySubmitted) {
          setVenueInfo(data);
          return;
        }
        setVenueInfo(data);
        setF(initFields(data));
      } catch {
        setLoadError('Failed to load venue information.');
      } finally {
        setLoading(false);
      }
    })();
  }, [dealId, vendorId, getRecaptchaToken]);

  const country = venueInfo?.country;

  const change = (key) => (e) => {
    const next = { ...f, [key]: e.target.value };
    setF(next);
    if (triedSubmit) {
      const nextErr = validate(next, country);
      if (!accept) nextErr.accept = 'You must accept the terms to submit.';
      setErr(nextErr);
    }
  };

  const toggleAccept = () => {
    const next = !accept;
    setAccept(next);
    if (triedSubmit) {
      const nextErr = validate(f, country);
      if (!next) nextErr.accept = 'You must accept the terms to submit.';
      setErr(nextErr);
    }
  };

  const handleSubmit = async () => {
    const newErr = validate(f, country);
    if (!accept) newErr.accept = 'You must accept the terms to submit.';
    setTriedSubmit(true);

    if (Object.keys(newErr).length > 0) {
      setErr(newErr);
      setTimeout(() => {
        const firstKey = ERROR_ORDER.find(k => newErr[k]);
        if (!firstKey) return;
        const el = refs.current[firstKey];
        if (!el) return;
        const container = scrollRef?.current ?? window;
        const containerTop = container === window ? 0 : container.getBoundingClientRect().top;
        const top = el.getBoundingClientRect().top - containerTop + (container === window ? window.scrollY : container.scrollTop) - 110;
        container.scrollTo({ top, behavior: 'smooth' });
        const input = el.querySelector('input, [role="checkbox"]');
        if (input) input.focus({ preventScroll: true });
      }, 60);
      return;
    }

    if (submittingRef.current) return;
    submittingRef.current = true;
    setErr({});
    setSubmitError(null);
    setSubmitting(true);
    try {
      const captchaToken = getRecaptchaToken ? await getRecaptchaToken() : null;
      const result = await CoreServiceSelector.GetSignupInfoService().postVendorSignup(dealId, vendorId, { ...f, dealId, vendorId }, captchaToken);
      if (!result?.success) {
        setSubmitError(result?.errorMessage || 'Submission failed. Please try again.');
        return;
      }
      setSubmitted(true);
      (scrollRef?.current ?? window).scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setSubmitError('Submission failed. Please try again.');
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const isFormReady = f !== null && Object.keys(validate(f, country)).length === 0 && accept;

  const isAlreadySubmitted = !!venueInfo?.isAlreadySubmitted;

  return {
    loading, loadError, venueInfo, isAlreadySubmitted,
    f, accept, err, triedSubmit, submitted,
    submitting, submitError,
    isFormReady,
    change, toggleAccept, handleSubmit,
    r,
  };
}
